// ══════════════════════════════════════════════════════════════════════════════
//  DigitalProductsScreen.jsx
//  المسار: src/screens/client/DigitalProductsScreen.jsx
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import supabaseService from '../services/supabaseService';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiArrowLeft, FiSearch, FiX, FiStar, FiShoppingBag,
  FiBook, FiArchive, FiLink, FiRefreshCw,
  FiWifi, FiBookOpen, FiEye, FiSliders, FiCheck
} from 'react-icons/fi';
import { getBackblazeDownloadUrl } from '../config/backblaze';

// ══════════════════════════════════════════════════════════════════════════════
//  مساعدات
// ══════════════════════════════════════════════════════════════════════════════
const SORT_OPTIONS = [
  { mode: 'newest',    label: 'الأحدث',        icon: '🕐' },
  { mode: 'topRated',  label: 'الأعلى تقييماً', icon: '⭐' },
  { mode: 'popular',   label: 'الأكثر مبيعاً',  icon: '🔥' },
  { mode: 'cheapest',  label: 'الأرخص أولاً',   icon: '⬇️' },
  { mode: 'expensive', label: 'الأغلى أولاً',   icon: '⬆️' },
];

const TYPE_OPTIONS = [
  { type: 'all',  label: 'الكل',      icon: <FiBookOpen className="w-3.5 h-3.5" /> },
  { type: 'pdf',  label: 'كتب PDF',   icon: <FiBook     className="w-3.5 h-3.5" /> },
  { type: 'zip',  label: 'ملفات ZIP', icon: <FiArchive  className="w-3.5 h-3.5" /> },
  { type: 'link', label: 'روابط',     icon: <FiLink     className="w-3.5 h-3.5" /> },
];

const PRICE_OPTIONS = [
  { id: 'all',      label: 'كل الأسعار',      test: () => true },
  { id: 'free',     label: 'مجاني',            test: p => p === 0 },
  { id: 'u500',     label: 'أقل من 500 د.ج',   test: p => p > 0 && p < 500 },
  { id: '500_2000', label: '500 - 2000 د.ج',   test: p => p >= 500 && p <= 2000 },
  { id: 'a2000',    label: 'أكثر من 2000 د.ج', test: p => p > 2000 },
];

const RATING_OPTIONS = [
  { id: 'all', label: 'كل التقييمات',   min: 0 },
  { id: '3',   label: '3 نجوم فأكثر',   min: 3 },
  { id: '4',   label: '4 نجوم فأكثر',   min: 4 },
  { id: '4.5', label: '4.5 نجوم فأكثر', min: 4.5 },
];

function typeIcon(t) {
  if (t === 'pdf')  return <FiBook    className="w-3 h-3" />;
  if (t === 'zip')  return <FiArchive className="w-3 h-3" />;
  if (t === 'link') return <FiLink    className="w-3 h-3" />;
  return <FiBookOpen className="w-3 h-3" />;
}
function typeLabel(t) {
  if (t === 'pdf')  return 'PDF';
  if (t === 'zip')  return 'ZIP';
  if (t === 'link') return 'رابط';
  return 'رقمي';
}

// هل يوجد محتوى معاينة؟
function hasPreview(s) {
  return !!s.previewUrl || (s.previewImages?.length > 0);
}

// لون زر المعاينة حسب النوع
function previewColor(t) {
  if (t === 'pdf')  return 'border-violet-200 bg-violet-50 text-violet-600 hover:bg-violet-100';
  if (t === 'zip')  return 'border-amber-200  bg-amber-50  text-amber-600  hover:bg-amber-100';
  if (t === 'link') return 'border-sky-200    bg-sky-50    text-sky-600    hover:bg-sky-100';
  return 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100';
}

// ══════════════════════════════════════════════════════════════════════════════
//  مودال معاينة الصور (ZIP / Link)
// ══════════════════════════════════════════════════════════════════════════════
function ImagePreviewModal({ images, title, onClose }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black flex flex-col"
      dir="rtl"
    >
      {/* header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-black/80 border-b border-white/10 flex-shrink-0">
        <button onClick={onClose}
          className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/10 text-white hover:bg-white/20 transition">
          <FiX className="w-4 h-4" />
        </button>
        <p className="flex-1 text-sm font-bold text-white truncate">{title} — معاينة</p>
        <span className="text-xs text-white/60 bg-white/10 px-2.5 py-1 rounded-full font-semibold">
          {current + 1} / {images.length}
        </span>
      </div>

      {/* الصورة الرئيسية */}
      <div className="flex-1 flex items-center justify-center overflow-hidden relative">
        <AnimatePresence mode="wait">
          <motion.img
            key={current}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.16 }}
            src={images[current]}
            alt={`preview-${current}`}
            className="max-w-full max-h-full object-contain select-none"
          />
        </AnimatePresence>

        {current > 0 && (
          <button onClick={() => setCurrent(c => c - 1)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-xl bg-black/50 text-white hover:bg-black/70 transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
        {current < images.length - 1 && (
          <button onClick={() => setCurrent(c => c + 1)}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-xl bg-black/50 text-white hover:bg-black/70 transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
      </div>

      {/* thumbnails */}
      {images.length > 1 && (
        <div className="flex gap-2 px-4 py-3 overflow-x-auto scrollbar-hide bg-black/60 flex-shrink-0">
          {images.map((img, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 transition-all
                ${i === current ? 'border-white scale-105' : 'border-white/20 opacity-60 hover:opacity-90'}`}>
              <img src={img} alt={`t-${i}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* dots */}
      {images.length > 1 && (
        <div className="flex justify-center gap-1.5 py-3 bg-black/60">
          {images.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`rounded-full transition-all ${i === current ? 'w-5 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/30'}`}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  زر المعاينة المشترك
// ══════════════════════════════════════════════════════════════════════════════
function PreviewBtn({ service, onPreview, size = 'md' }) {
  if (!hasPreview(service)) return null;
  const sm = size === 'sm' || size === 'xs';
  return (
    <button
      onClick={e => { e.stopPropagation(); onPreview(service); }}
      className={`w-full flex items-center justify-center gap-1 border rounded-lg font-bold transition-all duration-200
        ${size === 'xs' ? 'h-5 text-[8px]' : sm ? 'h-6 text-[9px]' : 'h-7 text-[10px]'}
        ${previewColor(service.productType)}`}
    >
      <FiEye className={size === 'xs' ? 'w-2 h-2' : 'w-2.5 h-2.5'} />
      {size === 'xs' ? 'معاينة' :
       service.productType === 'pdf'  ? 'معاينة PDF'   :
       service.productType === 'zip'  ? 'معاينة الملف' :
                                        'معاينة'}
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  بطاقة مصغّرة (تُستخدم فـ الشبكة العلوية "جميع المنتجات")
// ══════════════════════════════════════════════════════════════════════════════
function CompactProductCard({ service, inCart, isOwned, onToggleCart, onOpen, onPreview }) {
  const hasCover = service.imageUrl?.length > 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-xl overflow-hidden flex flex-col cursor-pointer select-none
        border transition-all duration-200 active:scale-[0.97]
        ${isOwned ? 'border-green-400 border-[1.3px]' : inCart ? 'border-gray-800 border-[1.1px]' : 'border-gray-200 border-[0.5px]'}`}
      style={{ boxShadow: '0 1px 6px rgba(0,0,0,.06)' }}
      onClick={() => onOpen(service)}
    >
      {/* غلاف */}
      <div className="relative w-full aspect-square bg-gray-100 flex-shrink-0">
        {hasCover
          ? <img src={service.imageUrl} alt={service.name} className="w-full h-full object-contain bg-gray-50"
              onError={e => { e.target.style.display = 'none'; }} />
          : <div className="w-full h-full flex items-center justify-center">
              <FiBook className="w-6 h-6 text-gray-200" />
            </div>
        }
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-1 right-1 flex items-center gap-0.5 bg-black/55 text-white text-[8px] font-semibold px-1.5 py-0.5 rounded">
          {typeIcon(service.productType)}
        </div>
        {service.averageRating > 0 && (
          <div className="absolute top-1 left-1 flex items-center gap-0.5 bg-black/55 text-white text-[8px] font-semibold px-1.5 py-0.5 rounded">
            <FiStar className="w-2 h-2 text-yellow-400" />
            <span>{service.averageRating.toFixed(1)}</span>
          </div>
        )}
        <span className="absolute bottom-1 left-1 text-white text-[10px] font-extrabold drop-shadow">
          {service.price > 0 ? `${Math.round(service.price)} د.ج` : 'مجاني'}
        </span>
        {isOwned && (
          <div className="absolute bottom-1 right-1 bg-green-500 rounded p-0.5">
            <FiCheck className="w-2 h-2 text-white" />
          </div>
        )}
      </div>

      {/* معلومات */}
      <div className="flex flex-col gap-1 p-1.5 flex-1">
        <p className="text-[10px] font-bold text-gray-900 leading-tight line-clamp-2">{service.name || 'منتج رقمي'}</p>
        <div className="mt-auto flex flex-col gap-1">
          <PreviewBtn service={service} onPreview={onPreview} size="xs" />
          <button
            onClick={e => { e.stopPropagation(); if (!isOwned) onToggleCart(service); }}
            className={`w-full h-6 rounded-md text-[8px] font-bold border transition-all duration-200
              ${isOwned ? 'bg-green-50 border-green-300 text-green-700 cursor-default'
              : inCart  ? 'bg-gray-900 border-gray-900 text-white'
              :           'bg-gray-50  border-gray-200  text-gray-600'}`}
          >
            {isOwned ? '✓ مشترى' : inCart ? 'في السلة' : '+ أضف'}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  بطاقة أفقية (للأقسام السفلية)
// ══════════════════════════════════════════════════════════════════════════════
function HorizontalCard({ service, inCart, isOwned, onToggleCart, onOpen, showRating, onPreview }) {
  const hasCover = service.imageUrl?.length > 0;
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className={`flex-shrink-0 w-36 rounded-2xl overflow-hidden cursor-pointer select-none bg-white
        border transition-all hover:shadow-lg active:scale-[0.98]
        ${isOwned ? 'border-green-400 border-[1.5px]' : inCart ? 'border-gray-800 border-[1.2px]' : 'border-gray-200 border-[0.5px]'}`}
      style={{ boxShadow: '0 2px 12px rgba(0,0,0,.10)' }}
      onClick={() => onOpen(service)}
    >
      <div className="relative w-full h-36 bg-gray-100">
        {hasCover
          ? <img src={service.imageUrl} alt={service.name} className="w-full h-full object-contain bg-gray-50"
              onError={e => { e.target.style.display = 'none'; }} />
          : <div className="w-full h-full flex items-center justify-center">
              <FiBook className="w-8 h-8 text-gray-200" />
            </div>
        }
        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/55 text-white text-[8px] font-semibold px-1.5 py-0.5 rounded">
          {typeIcon(service.productType)}<span>{typeLabel(service.productType)}</span>
        </div>
        {showRating && service.averageRating > 0 && (
          <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/55 text-white text-[8px] font-semibold px-1.5 py-0.5 rounded">
            <FiStar className="w-2 h-2 text-yellow-400" /><span>{service.averageRating.toFixed(1)}</span>
          </div>
        )}
        <span className="absolute bottom-2 left-2 text-white text-[11px] font-extrabold drop-shadow">
          {service.price > 0 ? `${Math.round(service.price)} د.ج` : 'مجاني'}
        </span>
        {isOwned && (
          <div className="absolute bottom-2 right-2 bg-green-500 rounded p-0.5">
            <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z"/>
            </svg>
          </div>
        )}
      </div>

      <div className="p-2 flex flex-col gap-1.5">
        <p className="text-[11px] font-bold text-gray-900 leading-tight line-clamp-2">{service.name}</p>
        <PreviewBtn service={service} onPreview={onPreview} size="sm" />
        <button
          onClick={e => { e.stopPropagation(); if (!isOwned) onToggleCart(service); }}
          className={`w-full h-6 rounded text-[9px] font-bold border transition-all
            ${isOwned ? 'bg-green-50 border-green-300 text-green-700 cursor-default'
            : inCart  ? 'bg-gray-900 border-gray-900 text-white'
            :           'bg-gray-50  border-gray-200  text-gray-600'}`}
        >
          {isOwned ? '✓ مشترى' : inCart ? 'في السلة' : '+ أضف'}
        </button>
      </div>
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Shimmer
// ══════════════════════════════════════════════════════════════════════════════
function ShimmerGrid() {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 p-4">
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="rounded-xl overflow-hidden border border-gray-100 bg-white animate-pulse">
          <div className="aspect-square bg-gray-100" />
          <div className="p-1.5 space-y-1.5">
            <div className="h-2 bg-gray-100 rounded w-4/5" />
            <div className="h-2 bg-gray-100 rounded w-3/5" />
            <div className="h-5 bg-gray-100 rounded-md w-full" />
            <div className="h-6 bg-gray-100 rounded-md w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  سلة الشراء
// ══════════════════════════════════════════════════════════════════════════════
function CartSheet({ cart, onRemove, onClose, uid, onSuccess }) {
  const [buying, setBuying] = useState(false);
  const [items, setItems]   = useState({ ...cart });
  const total = Object.values(items).reduce((s, i) => s + (i.price || 0), 0);

  const removeItem = (id) => {
    const next = { ...items };
    delete next[id];
    setItems(next);
    onRemove(id);
    if (Object.keys(next).length === 0) onClose();
  };

  const buy = async () => {
    if (!uid) { toast.error('يجب تسجيل الدخول أولاً'); return; }
    setBuying(true);
    const errors = [];
    const alreadyOwned = [];

    for (const s of Object.values(items)) {
      try {
        const owned = await supabaseService.hasClientPurchased({ clientId: uid, serviceId: s.id });
        if (owned) { alreadyOwned.push(s.name || 'منتج'); continue; }
        const detail = await supabaseService.client
          .from('services').select('provider_id').eq('id', s.id).single();
        await supabaseService.purchaseDigitalProduct({
          clientId: uid, serviceId: s.id,
          providerId: detail.data.provider_id, amount: s.price || 0,
        });
      } catch (e) {
        errors.push(s.name || s.id);
      }
    }

    setBuying(false);
    if (alreadyOwned.length) toast.error(`مشترى مسبقاً: ${alreadyOwned.join(', ')}`);
    if (!errors.length && !alreadyOwned.length) {
      toast.success('✓ تمت عملية الشراء بنجاح');
      onSuccess(); onClose();
    } else if (errors.length) {
      toast.error(`فشل: ${errors.join(', ')}`);
    }
  };

  return (
    <motion.div
      initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl border border-gray-200"
      style={{ boxShadow: '0 -8px 40px rgba(0,0,0,.14)' }}
    >
      <div className="flex justify-center pt-3 pb-1">
        <div className="w-8 h-1 bg-gray-200 rounded-full" />
      </div>
      <div className="px-5 pb-2 flex items-center justify-between">
        <h3 className="text-base font-extrabold text-gray-900">السلة</h3>
        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
          {Object.keys(items).length} منتج
        </span>
      </div>

      <div className="px-5 max-h-56 overflow-y-auto space-y-2 pb-2">
        {Object.values(items).map(s => (
          <div key={s.id} className="flex items-center gap-3 bg-gray-50 rounded-xl p-2.5 border border-gray-100">
            <div className="w-11 h-11 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
              {s.imageUrl
                ? <img src={s.imageUrl} alt={s.name} className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center">
                    <FiBook className="w-5 h-5 text-gray-400" />
                  </div>
              }
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-gray-900 truncate">{s.name}</p>
              <p className="text-xs font-extrabold text-gray-900 mt-0.5">{Math.round(s.price || 0)} د.ج</p>
            </div>
            <button onClick={() => removeItem(s.id)}
              className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-400 hover:text-red-500 transition">
              <FiX className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      <div className="px-5 pt-3 pb-6 space-y-3 border-t border-gray-100">
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-gray-400">الإجمالي</span>
          <span className="text-2xl font-black text-gray-900 tracking-tight">{Math.round(total)} د.ج</span>
        </div>
        <button
          onClick={buy} disabled={buying}
          className={`w-full py-3 rounded-xl font-extrabold text-sm flex items-center justify-center gap-2 transition-all
            ${buying ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gray-900 text-white hover:bg-gray-800 active:scale-[0.98]'}`}
          style={{ boxShadow: buying ? 'none' : '0 4px 14px rgba(0,0,0,.18)' }}
        >
          {buying
            ? <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            : <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                إتمام الشراء — {Math.round(total)} د.ج
              </>
          }
        </button>
      </div>
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  تبويبة الفلترة الجانبية
// ══════════════════════════════════════════════════════════════════════════════
function FilterPanel({
  sortMode, setSortMode,
  categories, selectedCat, setSelectedCat,
  typeFilter, setTypeFilter,
  priceFilter, setPriceFilter,
  ratingFilter, setRatingFilter,
  freeOnly, setFreeOnly,
  previewOnly, setPreviewOnly,
  onReset, onClose,
}) {
  return (
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/30 z-40 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="fixed top-0 right-0 z-50 h-full w-[85%] max-w-xs bg-white shadow-2xl flex flex-col"
        dir="rtl"
      >
        {/* هيدر التبويبة */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <h3 className="text-base font-black text-gray-900">فلترة وترتيب</h3>
          <div className="flex items-center gap-2">
            <button onClick={onReset}
              className="text-[11px] font-bold text-gray-400 hover:text-red-500 transition">
              إعادة تعيين
            </button>
            <button onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 border border-gray-200 text-gray-500 hover:bg-gray-200 transition">
              <FiX className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">

          {/* الترتيب */}
          <div>
            <p className="text-xs font-bold text-gray-400 mb-2.5">الترتيب حسب</p>
            <div className="flex flex-col gap-2">
              {SORT_OPTIONS.map(opt => (
                <button key={opt.mode} onClick={() => setSortMode(opt.mode)}
                  className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-sm font-bold border transition-all
                    ${sortMode === opt.mode ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
                >
                  <span className="text-sm">{opt.icon}</span>{opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* نوع المنتج */}
          <div>
            <p className="text-xs font-bold text-gray-400 mb-2.5">نوع المنتج</p>
            <div className="grid grid-cols-2 gap-2">
              {TYPE_OPTIONS.map(opt => (
                <button key={opt.type} onClick={() => setTypeFilter(opt.type)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-bold border transition-all
                    ${typeFilter === opt.type ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
                >
                  {opt.icon}{opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* نطاق السعر */}
          <div>
            <p className="text-xs font-bold text-gray-400 mb-2.5">السعر</p>
            <div className="flex flex-col gap-2">
              {PRICE_OPTIONS.map(opt => (
                <button key={opt.id} onClick={() => setPriceFilter(opt.id)}
                  className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-bold border transition-all
                    ${priceFilter === opt.id ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
                >
                  {opt.label}
                  {priceFilter === opt.id && <FiCheck className="w-4 h-4" />}
                </button>
              ))}
            </div>
          </div>

          {/* التقييم */}
          <div>
            <p className="text-xs font-bold text-gray-400 mb-2.5">التقييم</p>
            <div className="flex flex-col gap-2">
              {RATING_OPTIONS.map(opt => (
                <button key={opt.id} onClick={() => setRatingFilter(opt.id)}
                  className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-bold border transition-all
                    ${ratingFilter === opt.id ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
                >
                  <span className="flex items-center gap-1.5">
                    {opt.min > 0 && <FiStar className="w-3.5 h-3.5 text-yellow-400" />}
                    {opt.label}
                  </span>
                  {ratingFilter === opt.id && <FiCheck className="w-4 h-4" />}
                </button>
              ))}
            </div>
          </div>

          {/* التصنيفات */}
          {categories.length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-400 mb-2.5">التصنيف</p>
              <div className="flex flex-wrap gap-2">
                {[{ id: null, name: 'الكل' }, ...categories].map(cat => {
                  const sel = selectedCat === cat.id;
                  return (
                    <button key={cat.id ?? 'all'} onClick={() => setSelectedCat(cat.id)}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition-all
                        ${sel ? 'bg-gray-100 border-gray-800 text-gray-900' : 'bg-transparent border-gray-200 text-gray-400 hover:border-gray-300'}`}
                    >
                      {cat.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* خيارات إضافية */}
          <div>
            <p className="text-xs font-bold text-gray-400 mb-2.5">خيارات إضافية</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => setFreeOnly(v => !v)}
                className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-bold border transition-all
                  ${freeOnly ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
              >
                المنتجات المجانية فقط
                <span className={`w-9 h-5 rounded-full relative transition-colors flex-shrink-0
                  ${freeOnly ? 'bg-white/25' : 'bg-gray-200'}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all shadow
                    ${freeOnly ? 'right-0.5' : 'right-4'}`} />
                </span>
              </button>
              <button
                onClick={() => setPreviewOnly(v => !v)}
                className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-bold border transition-all
                  ${previewOnly ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
              >
                يحتوي على معاينة فقط
                <span className={`w-9 h-5 rounded-full relative transition-colors flex-shrink-0
                  ${previewOnly ? 'bg-white/25' : 'bg-gray-200'}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all shadow
                    ${previewOnly ? 'right-0.5' : 'right-4'}`} />
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* زر التطبيق */}
        <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <button onClick={onClose}
            className="w-full h-11 rounded-xl bg-gray-900 text-white text-sm font-bold hover:bg-gray-800 transition-colors">
            عرض النتائج
          </button>
        </div>
      </motion.div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  الشاشة الرئيسية
// ══════════════════════════════════════════════════════════════════════════════
export default function DigitalProductsScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const uid      = user?.id;

  const [all,         setAll]         = useState([]);
  const [filtered,    setFiltered]    = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [selectedCat, setSelectedCat] = useState(null);
  const [sortMode,    setSortMode]    = useState('newest');
  const [search,      setSearch]      = useState('');
  const [cart,        setCart]        = useState({});
  const [owned,       setOwned]       = useState(new Set());
  const [showCart,    setShowCart]    = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // ── فلاتر إضافية ─────────────────────────────────────
  const [typeFilter,    setTypeFilter]    = useState('all');   // all | pdf | zip | link
  const [priceFilter,   setPriceFilter]   = useState('all');   // PRICE_OPTIONS id
  const [ratingFilter,  setRatingFilter]  = useState('all');   // RATING_OPTIONS id
  const [freeOnly,      setFreeOnly]      = useState(false);
  const [previewOnly,   setPreviewOnly]   = useState(false);

  // مودال معاينة الصور
  const [previewModal, setPreviewModal] = useState(null); // { images, title }

  // ── تحميل ───────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [servicesResp, cats] = await Promise.all([
        supabaseService.client
          .from('services')
          .select('*, providers!inner(id, full_name, avatar_url, is_verified, available, professions(is_digital_products))')
          .not('product_type', 'is', null)
          .eq('is_active', true),
        supabaseService.getDigitalProductCategories(),
      ]);

      const services = (servicesResp.data || [])
        .filter(s => {
          const p = s.providers;
          return p?.is_verified && p?.available && p?.professions?.is_digital_products;
        })
        .map(s => ({
          id:                s.id,
          name:              s.name,
          description:       s.description,
          price:             s.price || 0,
          imageUrl:          s.image_url,
          productType:       s.product_type,
          averageRating:     s.average_rating || 0,
          previewImages:     s.preview_images || [],
          previewUrl:        s.preview_url,
          fileUrl:           s.file_url,
          digitalCategoryId: s.digital_category_id,
        }));

      setAll(services);
      setCategories(cats || []);

      if (uid) {
        const { data: purchases } = await supabaseService.client
          .from('digital_purchases').select('service_id').eq('client_id', uid);
        setOwned(new Set((purchases || []).map(p => p.service_id)));
      }
    } catch (e) {
      setError(e.message || 'حدث خطأ');
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => { load(); }, [load]);

  // ── فلترة وترتيب ───────────────────────────────────────
  useEffect(() => {
    const q = search.trim().toLowerCase();
    const priceOpt  = PRICE_OPTIONS.find(p => p.id === priceFilter)   || PRICE_OPTIONS[0];
    const ratingOpt = RATING_OPTIONS.find(r => r.id === ratingFilter) || RATING_OPTIONS[0];

    let result = all.filter(s => {
      const mQ       = !q || s.name?.toLowerCase().includes(q) || s.description?.toLowerCase().includes(q);
      const mCat     = !selectedCat || s.digitalCategoryId === selectedCat;
      const mType    = typeFilter === 'all' || s.productType === typeFilter;
      const mPrice   = priceOpt.test(s.price || 0);
      const mRating  = (s.averageRating || 0) >= ratingOpt.min;
      const mFree    = !freeOnly || (s.price || 0) === 0;
      const mPreview = !previewOnly || hasPreview(s);
      return mQ && mCat && mType && mPrice && mRating && mFree && mPreview;
    });

    if (sortMode === 'topRated')  result = [...result].sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0));
    if (sortMode === 'popular')   result = [...result].sort((a, b) => (b.price || 0) - (a.price || 0));
    if (sortMode === 'cheapest')  result = [...result].sort((a, b) => (a.price || 0) - (b.price || 0));
    if (sortMode === 'expensive') result = [...result].sort((a, b) => (b.price || 0) - (a.price || 0));

    setFiltered(result);
  }, [all, search, selectedCat, sortMode, typeFilter, priceFilter, ratingFilter, freeOnly, previewOnly]);

  const toggleCart = (s) => {
    if (owned.has(s.id)) return;
    setCart(prev => {
      const next = { ...prev };
      if (next[s.id]) delete next[s.id]; else next[s.id] = s;
      return next;
    });
  };

  const cartTotal = Object.values(cart).reduce((s, i) => s + (i.price || 0), 0);

  const openDetail = (service) => {
    navigate('/digital-product-detail', {
      state: { service, isOwned: owned.has(service.id), isInCart: !!cart[service.id] }
    });
  };

  // ── منطق المعاينة حسب النوع ────────────────────────────
  const handlePreview = (service) => {
    const productType = service.productType || 'pdf';
    const imgs = service.previewImages || [];

    if (productType === 'link') {
      if (imgs.length > 0) {
        setPreviewModal({ images: imgs, title: service.name || 'معاينة' });
      } else {
        toast.error('لا توجد معاينة متاحة');
      }
      return;
    }

    if (productType === 'zip') {
      if (service.previewUrl) {
        navigate(`/read?book=${service.id}`, {
          state: { pdfUrl: getBackblazeDownloadUrl(service.previewUrl) }
        });
      } else if (imgs.length > 0) {
        setPreviewModal({ images: imgs, title: service.name || 'معاينة' });
      } else {
        toast.error('لا توجد معاينة متاحة');
      }
      return;
    }

    // pdf (default)
    if (service.previewUrl) {
      navigate(`/read?book=${service.id}`, {
        state: { pdfUrl: getBackblazeDownloadUrl(service.previewUrl) }
      });
    } else {
      toast.error('لا توجد معاينة متاحة لهذا المنتج');
    }
  };

  const resetFilters = () => {
    setSortMode('newest');
    setSelectedCat(null);
    setTypeFilter('all');
    setPriceFilter('all');
    setRatingFilter('all');
    setFreeOnly(false);
    setPreviewOnly(false);
  };

  const activeFilterCount =
    (sortMode !== 'newest' ? 1 : 0) +
    (selectedCat !== null ? 1 : 0) +
    (typeFilter !== 'all' ? 1 : 0) +
    (priceFilter !== 'all' ? 1 : 0) +
    (ratingFilter !== 'all' ? 1 : 0) +
    (freeOnly ? 1 : 0) +
    (previewOnly ? 1 : 0);

  // أقسام سفلية (اختيارية) — تبقى كما هي كعرض أفقي إضافي
  const topRatedList = [...filtered]
    .sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0))
    .filter(s => (s.averageRating || 0) > 0).slice(0, 10);
  const pdfs  = filtered.filter(s => s.productType === 'pdf');
  const zips  = filtered.filter(s => s.productType === 'zip');
  const links = filtered.filter(s => s.productType === 'link');

  // ══════════════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" dir="rtl">

      {/* ── هيدر ─────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 pt-4 pb-0">

          <div className="flex items-center gap-3 mb-3">
            <button onClick={() => navigate(-1)}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 border border-gray-200 hover:bg-gray-200 transition flex-shrink-0">
              <FiArrowLeft className="w-4 h-4 text-gray-700" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-black text-gray-900 tracking-tight leading-tight">المتجر الرقمي</h1>
              <p className="text-xs text-gray-400">كتب · ملفات · روابط</p>
            </div>
            {filtered.length > 0 && (
              <span className="text-xs font-bold bg-gray-100 border border-gray-200 text-gray-700 px-2.5 py-1 rounded-full flex-shrink-0">
                {filtered.length}
              </span>
            )}
          </div>

          <div className="relative mb-2.5">
            <FiSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 w-4 h-4" />
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="ابحث عن كتاب أو منتج..."
              className="w-full h-10 bg-gray-100 border border-gray-200 rounded-xl pr-9 pl-9 text-sm text-gray-900 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300 transition"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500">
                <FiX className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 pb-3 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setShowFilterPanel(true)}
              className="relative flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-900 text-white text-xs font-bold hover:bg-gray-800 transition-colors flex-shrink-0"
            >
              <FiSliders className="w-3.5 h-3.5" />
              فلترة وترتيب
              {activeFilterCount > 0 && (
                <span className="flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-black">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {sortMode !== 'newest' && (
              <span className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full bg-gray-100 border border-gray-200 text-xs font-bold text-gray-700">
                <span className="text-[11px]">{SORT_OPTIONS.find(o => o.mode === sortMode)?.icon}</span>
                {SORT_OPTIONS.find(o => o.mode === sortMode)?.label}
              </span>
            )}
            {typeFilter !== 'all' && (
              <span className="flex-shrink-0 px-3 py-1.5 rounded-full bg-gray-100 border border-gray-200 text-xs font-bold text-gray-700">
                {TYPE_OPTIONS.find(o => o.type === typeFilter)?.label}
              </span>
            )}
            {priceFilter !== 'all' && (
              <span className="flex-shrink-0 px-3 py-1.5 rounded-full bg-gray-100 border border-gray-200 text-xs font-bold text-gray-700">
                {PRICE_OPTIONS.find(o => o.id === priceFilter)?.label}
              </span>
            )}
            {ratingFilter !== 'all' && (
              <span className="flex-shrink-0 px-3 py-1.5 rounded-full bg-gray-100 border border-gray-200 text-xs font-bold text-gray-700">
                {RATING_OPTIONS.find(o => o.id === ratingFilter)?.label}
              </span>
            )}
            {selectedCat !== null && (
              <span className="flex-shrink-0 px-3 py-1.5 rounded-full bg-gray-100 border border-gray-200 text-xs font-bold text-gray-700 truncate max-w-[120px]">
                {categories.find(c => c.id === selectedCat)?.name}
              </span>
            )}
            {freeOnly && (
              <span className="flex-shrink-0 px-3 py-1.5 rounded-full bg-gray-100 border border-gray-200 text-xs font-bold text-gray-700">
                مجاني فقط
              </span>
            )}
            {previewOnly && (
              <span className="flex-shrink-0 px-3 py-1.5 rounded-full bg-gray-100 border border-gray-200 text-xs font-bold text-gray-700">
                فيه معاينة
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── محتوى ────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto pb-32">
        {loading ? (
          <ShimmerGrid />
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 px-6 gap-4">
            <FiWifi className="w-10 h-10 text-gray-200" />
            <p className="text-sm text-gray-400 text-center">{error}</p>
            <button onClick={load}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 border border-gray-200 text-sm font-bold text-gray-700 hover:bg-gray-200 transition">
              <FiRefreshCw className="w-4 h-4" /> إعادة المحاولة
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-6 gap-3">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 border border-gray-200 flex items-center justify-center">
              <FiBookOpen className="w-6 h-6 text-gray-300" />
            </div>
            <p className="font-bold text-gray-700">لا توجد منتجات</p>
            <p className="text-xs text-gray-400">جرّب تغيير الفلاتر أو البحث</p>
            {activeFilterCount > 0 && (
              <button onClick={resetFilters}
                className="mt-1 px-4 py-2 rounded-xl bg-gray-900 text-white text-xs font-bold hover:bg-gray-800 transition">
                إعادة تعيين الفلاتر
              </button>
            )}
          </div>
        ) : (
          <div className="pb-4">

            {/* ── جميع المنتجات (شبكة مصغّرة فوق) ───────────── */}
            <section>
              <SectionHeader title="🛍 جميع المنتجات" subtitle={`${filtered.length} منتج`} />
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 px-4">
                {filtered.map((s, i) => (
                  <motion.div key={s.id}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.03, 0.3) }}
                  >
                    <CompactProductCard service={s} inCart={!!cart[s.id]} isOwned={owned.has(s.id)}
                      onToggleCart={toggleCart} onOpen={openDetail} onPreview={handlePreview} />
                  </motion.div>
                ))}
              </div>
            </section>

            {topRatedList.length > 0 && (
              <section className="mb-2">
                <SectionHeader title="⭐ الأعلى تقييماً" subtitle={`${topRatedList.length} منتج`} />
                <div className="flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide">
                  {topRatedList.map(s => (
                    <HorizontalCard key={s.id} service={s} inCart={!!cart[s.id]} isOwned={owned.has(s.id)}
                      onToggleCart={toggleCart} onOpen={openDetail} onPreview={handlePreview} showRating />
                  ))}
                </div>
              </section>
            )}

            {pdfs.length > 0 && (
              <section className="mb-2">
                <SectionHeader title="📚 الكتب" subtitle={`${pdfs.length} كتاب متاح`} />
                <div className="flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide">
                  {pdfs.map(s => (
                    <HorizontalCard key={s.id} service={s} inCart={!!cart[s.id]} isOwned={owned.has(s.id)}
                      onToggleCart={toggleCart} onOpen={openDetail} onPreview={handlePreview} />
                  ))}
                </div>
              </section>
            )}

            {zips.length > 0 && (
              <section className="mb-2">
                <SectionHeader title="🗜 الملفات والحزم" subtitle={`${zips.length} حزمة`} />
                <div className="flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide">
                  {zips.map(s => (
                    <HorizontalCard key={s.id} service={s} inCart={!!cart[s.id]} isOwned={owned.has(s.id)}
                      onToggleCart={toggleCart} onOpen={openDetail} onPreview={handlePreview} />
                  ))}
                </div>
              </section>
            )}

            {links.length > 0 && (
              <section className="mb-2">
                <SectionHeader title="🔗 الروابط الرقمية" subtitle={`${links.length} رابط`} />
                <div className="flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide">
                  {links.map(s => (
                    <HorizontalCard key={s.id} service={s} inCart={!!cart[s.id]} isOwned={owned.has(s.id)}
                      onToggleCart={toggleCart} onOpen={openDetail} onPreview={handlePreview} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      {/* ── FAB السلة ────────────────────────────────────── */}
      <AnimatePresence>
        {Object.keys(cart).length > 0 && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0, opacity: 0 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40"
          >
            <button onClick={() => setShowCart(true)}
              className="flex items-center gap-3 bg-gray-900 text-white px-5 py-3.5 rounded-full font-extrabold text-sm transition hover:bg-gray-800 active:scale-[0.96]"
              style={{ boxShadow: '0 6px 24px rgba(0,0,0,.22)' }}
            >
              <FiShoppingBag className="w-4 h-4" />
              السلة · {Object.keys(cart).length}
              <span className="bg-white/15 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                {Math.round(cartTotal)} د.ج
              </span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── CartSheet ────────────────────────────────────── */}
      <AnimatePresence>
        {showCart && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-40 backdrop-blur-sm"
              onClick={() => setShowCart(false)}
            />
            <CartSheet
              cart={cart} uid={uid}
              onRemove={id => setCart(prev => { const n = { ...prev }; delete n[id]; return n; })}
              onClose={() => setShowCart(false)}
              onSuccess={() => {
                setOwned(prev => new Set([...prev, ...Object.keys(cart)]));
                setCart({});
                setShowCart(false);
              }}
            />
          </>
        )}
      </AnimatePresence>

      {/* ── مودال معاينة الصور ───────────────────────────── */}
      <AnimatePresence>
        {previewModal && (
          <ImagePreviewModal
            images={previewModal.images}
            title={previewModal.title}
            onClose={() => setPreviewModal(null)}
          />
        )}
      </AnimatePresence>

      {/* ── تبويبة الفلترة الجانبية ───────────────────────── */}
      <AnimatePresence>
        {showFilterPanel && (
          <FilterPanel
            sortMode={sortMode} setSortMode={setSortMode}
            categories={categories} selectedCat={selectedCat} setSelectedCat={setSelectedCat}
            typeFilter={typeFilter} setTypeFilter={setTypeFilter}
            priceFilter={priceFilter} setPriceFilter={setPriceFilter}
            ratingFilter={ratingFilter} setRatingFilter={setRatingFilter}
            freeOnly={freeOnly} setFreeOnly={setFreeOnly}
            previewOnly={previewOnly} setPreviewOnly={setPreviewOnly}
            onReset={resetFilters}
            onClose={() => setShowFilterPanel(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ── عنوان القسم ───────────────────────────────────────────
function SectionHeader({ title, subtitle }) {
  return (
    <div className="px-4 pt-5 pb-2.5">
      <p className="text-sm font-extrabold text-gray-900 tracking-tight">{title}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
  );
}